import React, { useState, useContext } from "react";
import { useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import moment from "moment";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import Button from "@material-ui/core/Button";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import PersonIcon from "@material-ui/icons/Person";
import LocationIcon from "@material-ui/icons/Place";
import PaymentIcon from "@material-ui/icons/CreditCard";
import { compactTime } from "../AppCommon/util";
import { STATE_CULTURE } from "../Actions/stateActions";
import ActivityColor from "../Common/activityColor";
import { AppContext, OPEN_TOAST } from "../Context";
import { MY_BOOKINGS } from "../routes";
import Status from "./status";
import { Modal } from "@mantine/core";

const useStyles = makeStyles((theme) => ({
  booking: {
    border: `1px solid ${theme.app.iconDisabledColor}`,
    borderBottom: "none",
    background: theme.app.background[1],
    display: "grid",
    gridTemplateColumns: (p) => (p.isCancelled ? "1fr" : "1fr auto"),
    gridGap: (p) => (p.isCancelled ? "0px" : "8px"),
    alignItems: "center",
    width: "100%",
    "&:first-child": {
      borderRadius: "4px 4px 0 0",
    },
    "&:last-child": {
      borderRadius: "0 0 4px 4px",
      borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
    },
    "&:only-child": {
      borderRadius: "4px",
      borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
    },
    "@media(max-width: 1300px)": {
      "&:first-child": {
        borderRadius: 0,
      },
      "&:last-child": {
        borderRadius: 0,
        borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
      },
      "&:only-child": {
        borderRadius: 0,
        borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
      },
    },
    "@media(max-width: 600px)": {
      "&:first-child": {
        marginTop: "16px",
      },
      gridTemplateColumns: "1fr",
      marginBottom: "16px",
      borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
    },
  },
  content: { color: theme.app.labelColor, padding: "1rem" },
  contentText: { color: theme.app.labelColor, marginTop: "0.25rem" },
  titleText: {
    lineHeight: 1.2,
    "@media(max-width: 600px)": {
      fontSize: "1.1rem",
    },
  },
  bookingId: {
    fontSize: "0.875rem",
    lineHeight: 1.6,
  },
  heading: {
    fontSize: "1rem !important",
    fontWeight: 500,
  },
  subheading: {
    lineHeight: 1,
    textTransform: "capitalize",
    fontSize: "0.875rem !important",
  },
  info: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gridGap: "16px",
    alignItems: "center",
    marginTop: "16px",
  },
  infoItem: {
    display: "flex",
    alignItems: "center",
    lineHeight: 1.5,
  },
  infoIcon: { width: "16px", height: "16px" },
  infoText: { fontSize: "0.8125rem", marginLeft: "4px" },
  actionContainer: {
    height: "100%",
    display: "flex",
    alignItems: "flex-end",
    padding: "8px",
    "@media(max-width: 600px)": {
      padding: "4px 0",
      // borderTop: `1px solid ${theme.app.iconDisabledColor}`,
      justifyContent: "flex-end",
    },
  },
  cancelButton: { color: theme.app.dangerColor },
  cancelDialog: {
    minWidth: "500px",
    minHeight: "250px",
    "@media(max-width: 600px)": { minWidth: "initial", minHeight: "initial" },
  },
  closeButton: {
    color: theme.app.labelColor,
  },
  noPadding: {
    padding: 0,
  },
}));

const CANCEL_BOOKING = gql`
  mutation Cancel($id: String) {
    cancelReservation(bookingId: $id) {
      position
      errorMessage
      failed
    }
  }
`;

const Booking = ({ booking }) => {
  const classes = useStyles({ isCancelled: booking.isCancelled });
  const [cancelOpen, setCancelOpen] = useState(false);
  const { language, culture } = useSelector((state) => ({
    language: state.language,
    culture: state.cookies[STATE_CULTURE],
  }));
  const { activity, occasion, remainingPaymentInclVat } = booking;
  const { location, name } = activity;
  const locale = culture.substring(0, 2).toLowerCase();
  const at = occasion
    ? compactTime(moment(occasion.at), locale, language)
    : "????";
  const lastPaymentTime = moment(booking.lastPaymentTime);
  const context = useContext(AppContext);
  const { dispatch } = context;
  const handleMutationError = (error) => {
    console.log(error);
    setCancelOpen(false);
    dispatch({
      type: OPEN_TOAST,
      toastText: language.errorMsg,
      toastType: "error",
    });
  };
  const handleMutationSuccess = ({ cancelReservation }) => {
    const { failed, errorMessage } = cancelReservation;
    setCancelOpen(false);
    if (failed) {
      return dispatch({
        type: OPEN_TOAST,
        toastText: errorMessage,
        toastType: "error",
      });
    }
    // dispatch({
    //   type: OPEN_TOAST,
    //   toastText: "Avbokningen lyckades!",
    //   toastType: "success",
    // });
  };
  const [cancelBooking, { loading }] = useMutation(CANCEL_BOOKING, {
    variables: { id: booking.id },
    onError: handleMutationError,
    onCompleted: handleMutationSuccess,
    refetchQueries: () => [{ query: MY_BOOKINGS }],
  });

  return (
    <li style={{ marginTop: "-1px" }}>
      <div className={classes.booking}>
        <div className={classes.content}>
          <Typography variant="h2" className={classes.titleText}>
            {name}
          </Typography>
          <Typography className={classes.bookingId}>{booking.id}</Typography>
          <Typography variant="h3" className={classes.subheading}>
            {at}
          </Typography>
          <Status booking={booking} />
          {booking.isQueue && booking.queueSlot && (
            <Typography
              className={classes.subheading}
            >{`${language.queueSlot} ${booking.queueSlot}/${occasion.maxQueueSlots}`}</Typography>
          )}
          <div className={classes.info}>
            <ActivityColor color={activity.color} />
            <div className={classes.bookingInfo}>
              <div className={classes.infoItem}>
                <LocationIcon className={classes.infoIcon} />
                <div className={classes.infoText}>{location.name}</div>
              </div>
              {booking.participants
                .filter((x) => x.id !== booking.responsible.id)
                .map((p, i) => {
                  return (
                    <div key={i} className={classes.infoItem}>
                      <PersonIcon className={classes.infoIcon} />
                      <div className={classes.infoText}>{p.name}</div>
                    </div>
                  );
                })}
              {remainingPaymentInclVat > 0 && !booking.isCancelled && (
                <div className={classes.infoItem}>
                  <PaymentIcon className={classes.infoIcon} />
                  <div className={classes.infoText}>
                    {booking.isPaid
                      ? language.is_paid
                      : `${remainingPaymentInclVat}:- (${
                          language.payAt
                        } ${compactTime(lastPaymentTime, locale, language)})`}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {booking.canCancel && (
          <div className={classes.actionContainer}>
            <Button
              size="small"
              className={classes.cancelButton}
              onClick={() => setCancelOpen(true)}
            >
              {language.cancel}
            </Button>
          </div>
        )}
      </div>
      <Modal
        classNames={{ close: classes.closeButton, inner: classes.noPadding }}
        opened={cancelOpen}
        closeButtonLabel={language.closeMenus}
        size="lg"
        style={{ zIndex: 8 }}
        centered
        onClose={() => setCancelOpen(false)}
        // fullScreen={window.innerWidth < 601}
        title={language.cancel}
      >
        <main>
          <Typography
            variant="h1"
            className={classes.heading}
            id="form-dialog-title"
          >
            {activity.name}
          </Typography>
          <DialogContent style={{ padding: 0 }}>
            <DialogContentText className={classes.contentText}>
              {`${language.cancel_booking_text} ${activity.name} - ${at} ?`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              className={classes.button}
              onClick={() => setCancelOpen(false)}
            >
              {language.abort}
            </Button>
            <Button
              size="small"
              className={classes.cancelButton}
              onClick={cancelBooking}
              disabled={loading}
            >
              {language.cancel}
            </Button>
          </DialogActions>
        </main>
      </Modal>
    </li>
  );
};

export default Booking;
